/* import __COLOCATED_TEMPLATE__ from './linked-transfer-instruments.hbs'; */
var _class, _descriptor;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { TransferEntityVerificationState, BalanceActivityType } from '@square/dashboard-protos/models/squareup/deposits/model';
import { getDisplayName as getCardBrandName } from '@square/dashboard-data/utils/card-brand';
import { Brand } from '@square/dashboard-protos/models/squareup/simple_instrument_store/model';
/**
 * Component for displaying and managing linked transfer instruments (bank accounts and debit cards).
 *
 * This component renders a list of transfer instruments that can be used for transfers,
 * showing their current status and providing actions to manage them.
 *
 * @example
 * ```hbs
 * <Banking::Settings::LinkedTransferInstruments
 *   @debitCardTransferInstrumentsWithPriority={{this.debitCardInstruments}}
 *   @bankAccountTransferInstrumentsWithPriority={{this.bankAccountInstruments}}
 *   @instantDepositFeeBasisPoints={{this.instantDepositFeeBasisPoints}}
 *   @isChecking={{this.isChecking}}
 *   @onCancelBankVerification={{this.handleCancelBankVerification}}
 *   @onLinkBankAccount={{this.handleLinkBankAccount}}
 *   @onLinkDebitCard={{this.handleLinkDebitCard}}
 *   @onResendBankVerificationEmail={{this.handleResendBankVerificationEmail}}
 *   @onResendDebitCardVerificationEmail={{this.handleResendDebitCardVerificationEmail}}
 * />
 * ```
 *
 * @param {boolean} isChecking - Whether the current context is for checking
 * @param {TransferInstrumentWithPriority[]} debitCardTransferInstrumentsWithPriority - Array of debit card transfer instruments with priority information
 * @param {TransferInstrumentWithPriority[]} bankAccountTransferInstrumentsWithPriority - Array of bank account transfer instruments with priority information
 * @param {number} [instantDepositFeeBasisPoints] - The fee basis points for the transfer
 * @param {Function} onCancelBankVerification - Callback function triggered when bank verification button is clicked
 * @param {Function} onLinkBankAccount - Callback function triggered when link a bank account button is clicked
 * @param {Function} onLinkDebitCard - Callback function triggered when link a debit card button is clicked
 * @param {Function} onResendBankVerificationEmail - Callback function triggered when resend bank verification email button is clicked
 * @param {Function} onResendDebitCardVerificationEmail - Callback function triggered when resend debit card verification email button is clicked
 */
let LinkedTransferInstrumentsComponent = (_class = class LinkedTransferInstrumentsComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
  }
  get feePercentage() {
    const feePercentage = this.args.instantDepositFeeBasisPoints ?? 0;
    return feePercentage / 100;
  }
  get sortedDebitCardInstruments() {
    const instruments = this.args.debitCardTransferInstrumentsWithPriority || [];
    return [...instruments].sort((transferInstrument1, transferInstrument2) => transferInstrument1.priority - transferInstrument2.priority);
  }
  get sortedBankAccountInstruments() {
    const instruments = this.args.bankAccountTransferInstrumentsWithPriority || [];
    return [...instruments].sort((transferInstrument1, transferInstrument2) => transferInstrument1.priority - transferInstrument2.priority);
  }
  get debitCardEntity() {
    return this.sortedDebitCardInstruments.find(transferInstrument => transferInstrument.transferInstrument?.cardEntity)?.transferInstrument?.cardEntity;
  }
  get bankAccountTransferInstrument() {
    return this.sortedBankAccountInstruments.find(transferInstrument => transferInstrument.transferInstrument?.bankAccountEntity)?.transferInstrument;
  }
  get bankAccountEntity() {
    return this.bankAccountTransferInstrument?.bankAccountEntity;
  }
  get isBankAccountInstantTransferSupported() {
    return this.bankAccountTransferInstrument?.supportedBalanceActivityTypes.includes(BalanceActivityType.INSTANT_PAYOUT);
  }
  get bankAccountEntitySecondPriority() {
    return this.sortedBankAccountInstruments[1]?.transferInstrument?.bankAccountEntity;
  }
  get bankAccountSubText() {
    if (this.args.isChecking) {
      if (!this.bankAccountTransferInstrument || this.isBankAccountInstantTransferSupported) {
        if (this.feePercentage) {
          return this.intl.t('banking.components.linkedTransferInstruments.bankAccount.subtext.checking.instrumentPresentITWithFee', {
            feePercentage: this.feePercentage
          });
        } else {
          return this.intl.t('banking.components.linkedTransferInstruments.bankAccount.subtext.checking.instrumentPresentIT');
        }
      } else {
        return this.intl.t('banking.components.linkedTransferInstruments.bankAccount.subtext.checking.instrumentPresent');
      }
    } else {
      if (!this.bankAccountTransferInstrument || this.isBankAccountInstantTransferSupported) {
        if (this.feePercentage) {
          return this.intl.t('banking.components.linkedTransferInstruments.bankAccount.subtext.balance.instrumentPresentITWithFee', {
            feePercentage: this.feePercentage
          });
        } else {
          return this.intl.t('banking.components.linkedTransferInstruments.bankAccount.subtext.balance.instrumentPresentIT');
        }
      } else {
        return this.intl.t('banking.components.linkedTransferInstruments.bankAccount.subtext.balance.instrumentPresent');
      }
    }
  }
  get debitCardSubText() {
    if (this.args.isChecking) {
      if (this.feePercentage) {
        return this.intl.t('banking.components.linkedTransferInstruments.debitCard.subtext.checkingWithFee', {
          feePercentage: this.feePercentage
        });
      } else {
        return this.intl.t('banking.components.linkedTransferInstruments.debitCard.subtext.checking');
      }
    } else {
      if (this.feePercentage) {
        return this.intl.t('banking.components.linkedTransferInstruments.debitCard.subtext.balanceWithFee', {
          feePercentage: this.feePercentage
        });
      } else {
        return this.intl.t('banking.components.linkedTransferInstruments.debitCard.subtext.balance');
      }
    }
  }
  getStatusPropsFromVerificationStatus(verificationStatus) {
    switch (verificationStatus) {
      case TransferEntityVerificationState.AWAITING_2FA_CONFIRMATION:
        return {
          text: this.intl.t('banking.components.linkedTransferInstruments.statuses.verifyToTransfer'),
          variant: 'warning'
        };
      case TransferEntityVerificationState.PENDING_VERIFICATION:
        return {
          text: this.intl.t('banking.components.linkedTransferInstruments.statuses.pendingVerification'),
          variant: 'warning'
        };
      case TransferEntityVerificationState.VERIFICATION_EXPIRED:
        return {
          text: this.intl.t('banking.components.linkedTransferInstruments.statuses.verificationExpired'),
          variant: 'critical'
        };
      default:
        return undefined;
    }
  }
  get debitCardPrimaryActionButton() {
    if (!this.debitCardEntity) {
      return {
        label: this.intl.t('banking.components.linkedTransferInstruments.actions.linkADebitCard'),
        rank: 'primary',
        action: this.args.onLinkDebitCard
      };
    }
    switch (this.debitCardEntity.verificationStatus) {
      case TransferEntityVerificationState.VERIFIED:
        return {
          label: this.intl.t('banking.components.linkedTransferInstruments.actions.updateDebitCard'),
          rank: 'secondary',
          action: this.args.onLinkDebitCard
        };
      case TransferEntityVerificationState.AWAITING_2FA_CONFIRMATION:
        return {
          label: this.intl.t('banking.components.linkedTransferInstruments.actions.resendEmail'),
          rank: 'primary',
          action: this.args.onResendDebitCardVerificationEmail
        };
      case TransferEntityVerificationState.VERIFICATION_EXPIRED:
        return {
          label: this.intl.t('banking.components.linkedTransferInstruments.actions.updateDebitCard'),
          rank: 'primary',
          action: this.args.onLinkDebitCard
        };
    }
    return undefined;
  }
  get debitCardSecondaryActionButton() {
    if (this.debitCardEntity?.verificationStatus === TransferEntityVerificationState.AWAITING_2FA_CONFIRMATION) {
      return {
        label: this.intl.t('banking.components.linkedTransferInstruments.actions.updateDebitCard'),
        rank: 'secondary',
        action: this.args.onLinkDebitCard
      };
    }
    return undefined;
  }
  get bankAccountPrimaryActionButton() {
    const bankAccountEntity = this.bankAccountEntity;
    if (!bankAccountEntity) {
      return {
        label: this.intl.t('banking.components.linkedTransferInstruments.actions.linkABankAccount'),
        rank: 'primary',
        action: this.args.onLinkBankAccount
      };
    }
    switch (bankAccountEntity.verificationStatus) {
      case TransferEntityVerificationState.VERIFIED:
        return {
          label: this.intl.t('banking.components.linkedTransferInstruments.actions.updateBankAccount'),
          rank: 'secondary',
          action: this.args.onLinkBankAccount
        };
      // Pending 2FA Verification
      case TransferEntityVerificationState.AWAITING_2FA_CONFIRMATION:
        return {
          label: this.intl.t('banking.components.linkedTransferInstruments.actions.resendEmail'),
          rank: 'primary',
          action: () => this.args.onResendBankVerificationEmail(bankAccountEntity.instrumentToken)
        };
      // Pending Microdeposit Verification
      case TransferEntityVerificationState.PENDING_VERIFICATION:
        return {
          label: this.intl.t('banking.components.linkedTransferInstruments.actions.cancelVerification'),
          rank: 'secondary',
          variant: 'destructive',
          action: () => this.args.onCancelBankVerification(bankAccountEntity)
        };
    }
    return undefined;
  }
  get bankAccountSecondaryActionButton() {
    const bankAccountEntity = this.bankAccountEntity;
    if (bankAccountEntity?.verificationStatus === TransferEntityVerificationState.AWAITING_2FA_CONFIRMATION) {
      return {
        label: this.intl.t('banking.components.linkedTransferInstruments.actions.cancelVerification'),
        rank: 'secondary',
        variant: 'destructive',
        action: () => this.args.onCancelBankVerification(bankAccountEntity)
      };
    }
    return undefined;
  }
  get debitCardRowProps() {
    return {
      title: this.debitCardEntity ? this.intl.t('banking.components.linkedTransferInstruments.debitCard.titleWithCard', {
        brand:
        // Util from dashboard-data/utils/card-brand works for cards
        // on https://prototype.sqprod.co/#/docs/squareup.simple_instrument_store.Brand
        getCardBrandName(Brand[this.debitCardEntity.cardBrand], this.intl) || this.intl.t('common.cardBrands.unknown'),
        panSuffix: this.debitCardEntity.panSuffix
      }) : this.intl.t('banking.components.linkedTransferInstruments.debitCard.titleDefault'),
      subtext: this.debitCardSubText,
      status: this.getStatusPropsFromVerificationStatus(this.debitCardEntity?.verificationStatus),
      primaryActionButton: this.debitCardPrimaryActionButton,
      secondaryActionButton: this.debitCardSecondaryActionButton
    };
  }
  get bankAccountRowProps() {
    return {
      title: this.bankAccountEntity ? this.intl.t('banking.components.linkedTransferInstruments.bankAccount.titleWithBank', {
        bankName: this.bankAccountEntity.bankName,
        accountNumberSuffix: this.bankAccountEntity.accountNumberSuffix
      }) : this.intl.t('banking.components.linkedTransferInstruments.bankAccount.titleDefault'),
      subtext: this.bankAccountSubText,
      additionalSubtext: this.sortedBankAccountInstruments.length > 1 && this.bankAccountEntitySecondPriority ? this.intl.t('banking.components.linkedTransferInstruments.bankAccount.tertiarySubtext.pendingVerification', {
        bankName: this.bankAccountEntitySecondPriority.bankName,
        accountNumberSuffix: this.bankAccountEntitySecondPriority.accountNumberSuffix
      }) : undefined,
      status: this.getStatusPropsFromVerificationStatus(this.bankAccountEntity?.verificationStatus),
      primaryActionButton: this.bankAccountPrimaryActionButton,
      secondaryActionButton: this.bankAccountSecondaryActionButton
    };
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { LinkedTransferInstrumentsComponent as default };